import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { flow, get, isEmpty } from 'lodash-es';
import { merge } from 'client/site-modules/shared/components/lead-form/utils/merge';
import { LEAD_TYPES, SUB_HEADER_COMPONENT } from 'site-modules/shared/constants/lead-form/lead-form-params';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { UnifiedLeadForm } from 'site-modules/shared/components/lead-form/unified-lead-form/unified-lead-form';
import {
  enrichLeadsWithAdditionalData,
  getLeadSubmissionInfoWithoutEmail,
  getPathname,
} from 'site-modules/shared/components/lead-form/utils/utils';
import {
  sendMarketoThankYouEmail,
  sendUsedInventoryMarketoThankYouEmail,
} from 'site-modules/shared/components/lead-form/lead-form-wrapper/lead-form-wrapper-methods';
import { CTA_TYPE } from 'site-modules/shared/constants/inventory/lead-form';
import { getLeadFormCreativeId } from 'client/site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildLeadTargetsPath, LeadTargetsModel } from 'client/data/models/lead-targets';
import { VisitorModel } from 'client/data/models/visitor';
import { TopEmbeddedFormView } from 'site-modules/shared/components/lead-form/unified-lead-form/views/form-view/top-embedded/top-embedded-form-view';
import { STRATEGIES } from 'site-modules/shared/constants/lead-form/lead-form-configs';
import { hideEmbeddedLeadForm } from 'site-modules/shared/utils/lead-form/hide-embedded-lead-form';
import { getCarZingTags } from 'site-modules/shared/components/inventory/utils/carzing';
import { isCarMax } from 'site-modules/shared/utils/dealer-details-utils';
import { isMakePorsche } from 'site-modules/shared/utils/inventory/make';
import { EmbeddedLfEmptyView } from 'site-modules/shared/components/lead-form/unified-lead-form/views/empty-view/embedded-lf-empty-view/embedded-lf-empty-view';
import { useLeadFormScreenState } from 'site-modules/shared/components/lead-form/unified-lead-form-pages/embedded-lead-form-utils';
import { getPlural } from 'client/utils/plural';

import './embedded-lead-form.scss';

const SCROLL_MAP = [
  { input: 'firstName', selector: '#lead-form-pricing-fname' },
  { input: 'lastName', selector: '#lead-form-pricing-lname' },
  { input: 'email', selector: '#lead-form-pricing-email' },
  { input: 'phone', selector: '#lead-form-pricing-phone' },
];

const SCROLL_MAP_EMBEDDED = [
  { input: 'firstName', selector: '#lead-form-embeded-fname' },
  { input: 'lastName', selector: '#lead-form-embeded-lname' },
  { input: 'email', selector: '#lead-form-embeded-email' },
  { input: 'phone', selector: '#lead-form-embeded-phone' },
];

const getNciDaysToSellSubHeader = daysToSell => {
  let subHeader;

  if (Number.isFinite(daysToSell)) {
    if (daysToSell >= 1 && daysToSell <= 30) {
      const flame = daysToSell <= 14 ? '🔥 ' : '';
      subHeader = `${flame}This car is likely to sell in ${daysToSell} ${getPlural('day', daysToSell)}`;
    } else if (daysToSell >= -14 && daysToSell <= 0) {
      subHeader = '🔥 This car is likely to sell any day now';
    } else if (daysToSell <= -15) {
      subHeader = 'Let the dealer know you’re interested';
    }
  }

  return subHeader;
};

const getCtaConfig = (unifiedLeadFormCTA, { dealerName, make, isShop5146NciChallenge, daysToSell }) => {
  const getNewVinCTASubHeadingText = () => {
    if (isMakePorsche(make)) {
      return 'Let the dealer know you’re interested';
    }

    return !isCarMax(dealerName) ? 'Contact dealer to get their best offer' : '';
  };

  const EMBEDDED_CTA_CONFIGS = {
    [CTA_TYPE.NEW.BASE]: {
      leadFormConfig: {
        submitButtonText: 'Get Dealer Offer',
        headerConfig: {
          header: {
            text: isMakePorsche(make) ? 'Check Availability' : 'Check Pricing',
          },
          subHeader: {
            text: getNewVinCTASubHeadingText(),
          },
        },
      },
      thankYouConfig: {
        headerConfig: {
          subHeader: {
            explainingText:
              'Thanks for submitting your request for the {year} {make} {model}. Your information has been sent and the dealer should follow up shortly',
          },
        },
      },
    },
    [CTA_TYPE.USED.BASE]: {
      leadFormConfig: {
        scrollMap: SCROLL_MAP_EMBEDDED,
        headerConfig: {
          header: {
            text: 'Check Availability',
          },
          subHeader: {
            text: '',
          },
        },
      },
      thankYouConfig: {
        headerConfig: {
          subHeader: {
            explainingText: 'Your request has been sent and the dealer should follow up shortly.',
          },
        },
      },
    },
  };
  const ctaConfig = EMBEDDED_CTA_CONFIGS[unifiedLeadFormCTA];
  const subHeaderText = isShop5146NciChallenge && getNciDaysToSellSubHeader(daysToSell);

  if (isShop5146NciChallenge && unifiedLeadFormCTA === CTA_TYPE.NEW.BASE) {
    return merge({}, ctaConfig, {
      leadFormConfig: {
        headerConfig: {
          header: { text: 'Check Availability' },
          ...(subHeaderText && { subHeader: { text: subHeaderText } }),
        },
      },
    });
  }

  return ctaConfig;
};

function EmbeddedLeadFormUI({
  params = {
    tcpaParentDealershipName: '',
  },
  options,
  pageNameLegacy = '',
  className = '',
  isMobile = false,
  leadTargets: leadTargetsProp,
  isVdp = false,
  isShop5146NciChallenge = false,
  styles = {},
  isUserSetZip = false,
  stateCode,
}) {
  const {
    vin,
    make,
    model,
    year,
    zipCode,
    radius,
    styleId,
    submodelIdentifier,
    isNewVin,
    tcpaParentDealershipName,
    dealerName,
    isPartnerListing,
    partnerCode,
    isLease,
    type,
  } = params;
  const {
    unifiedLeadFormCTA,
    daysToSell,
    hideContactDealerTip,
    buyonline,
    isShop4713Enabled,
    saveLeadDataForPrequal,
    shop4713CreativeId,
    isSimulatedLeadForm,
  } = options;
  const showZip = hideEmbeddedLeadForm({ isVdp, isPartnerListing, isUserSetZip, zipCode, stateCode });
  const { shouldRender, leadTargets, isLoading, showZipForm, noLeadTargets, onZipSubmit } = useLeadFormScreenState(
    leadTargetsProp,
    showZip
  );

  if (!shouldRender) {
    return null;
  }

  const ctaConfig = getCtaConfig(unifiedLeadFormCTA, {
    make,
    dealerName,
    isShop5146NciChallenge,
    daysToSell,
  });

  const creativeId = getLeadFormCreativeId({
    isNewVin,
    pageNameLegacy,
    formName: LEAD_FORMS_CREATIVE_ID.SINGLE_VDP_PRICING_EMBEDDED_FORM,
  });

  const leadAdditionalData = isLease ? { interest: 'lease' } : {};

  const customConfig = {
    creativeId,
    leadFormComponent: TopEmbeddedFormView,
    leadType: LEAD_TYPES.INVENTORY,
    leadFormConfig: {
      withFormClasses: false,
      hideWhenLeadTargetsEmpty: true,
      headerConfig: {
        headerComponent: null,
        header: {
          explainingText: '',
        },
      },
      tcpaParentDealershipName,
      submitButtonColor: isVdp ? 'blue-50' : 'primary-b',
      isSimulatedLeadForm,
    },
    processDataMethods: {
      postProcessMethods: [isNewVin ? sendMarketoThankYouEmail : sendUsedInventoryMarketoThankYouEmail],
      getLeadSubmissionInfo: flow(getLeadSubmissionInfoWithoutEmail, enrichLeadsWithAdditionalData(leadAdditionalData)),
    },
    thankYouConfig: {
      withFormClasses: false,
      headerConfig: {
        header: {
          showLogo: false,
        },
        subHeaderComponent: SUB_HEADER_COMPONENT.BASE,
        subHeader: {
          text: "You're All Set",
          insiderLinkOut: true,
        },
      },
    },
    hideDelighted: false,
  };

  // buyOnlineHeaderConfig will apply only for buyonline version of VDP
  const buyOnlineHeaderConfig = {
    leadFormConfig: {
      headerConfig: {
        header: {
          text: 'Contact Dealer for More Info',
        },
      },
    },
  };
  const shop4713Config = {
    creativeId: shop4713CreativeId,
    processDataMethods: {
      getLeadSubmissionInfo: flow(
        getLeadSubmissionInfoWithoutEmail,
        enrichLeadsWithAdditionalData({ comments: 'This customer is interested in pre-qualifying for financing.' })
      ),
      postProcessMethods: [sendUsedInventoryMarketoThankYouEmail, saveLeadDataForPrequal].filter(Boolean),
    },
    leadFormConfig: {
      headerConfig: {
        header: {
          text: 'See If You Pre-qualify for Financing',
          explainingText:
            'Connect with a dealer to get pre-qualified for financing. There is no impact to your credit score.',
        },
      },
    },
    thankYouConfig: {
      className: 'text-center pb-1_5',
      headerConfig: {
        header: {
          text: '',
          showLogo: false,
          showHeader: false,
        },
        subHeaderComponent: SUB_HEADER_COMPONENT.BASE,
        subHeader: {
          text: 'You’re one step closer to getting pre-qualified',
          insiderLinkOut: false,
          explainingText:
            'Your request has been sent to the dealer. Next, check your real rates from Capital One with no impact to your credit score.',
          closeButtonCTA: 'Continue',
          closeButtonClassNames:
            'btn btn-blue-50 text-white text-decoration-none text-transform-none fw-medium px-2 py-0_75',
          closeButtonUrl: `/financing/${vin}/basics/`,
          closeButtonTrackingId: 'open_financing_app',
        },
      },
    },
  };

  const getCustomConfig = () => {
    if (isShop4713Enabled) {
      return merge(customConfig, ctaConfig, shop4713Config);
    }
    if (buyonline) {
      return merge(merge(customConfig, ctaConfig), buyOnlineHeaderConfig);
    }
    return merge(customConfig, ctaConfig);
  };

  const formProps = {
    vin,
    make,
    model,
    year,
    zipCode,
    radius,
    styleId,
    submodelIdentifier,
    pathname: getPathname(options),
    customConfig: getCustomConfig(),
  };

  const topEmbeddedLeadFormConfig = {
    customConfig: {
      leadFormComponent: TopEmbeddedFormView,
      leadFormConfig: {
        submitButtonText: 'Request Information',
        scrollMap: SCROLL_MAP,
        headerConfig: {
          header: {
            className: 'heading-3 mb-0 text-center',
            tag: 'h2',
          },
        },
        daysToSell,
        isMobile,
        leadFormId: 'top-embedded-lead-form',
        hideContactDealerTip,
        buyonline,
        dealerName,
        isNewVin,
        isLoading,
        type,

        showZipForm,
        onZipSubmit,
        creativeId,
      },
      thankYouConfig: {
        adsConfig: { adComponent: null },
        headerConfig: {
          subHeader: {
            insiderBtnClasses: isVdp ? 'btn-blue-50' : 'btn-primary-b',
          },
        },
      },
      emptyLeadFormComponent: EmbeddedLfEmptyView,
      useLeadTargetsFromProps: true,
      noLeadTargets,
      renderFormServerSide: true,
      tags: getCarZingTags(partnerCode),
    },
  };

  const topEmbeddedLeadFormProps = merge({}, formProps, topEmbeddedLeadFormConfig);

  return (
    <section
      className={classNames('inline-embedded-lead-form', className || 'px-1_5 bg-cool-gray-100 pt-1_5 pb-0_5')}
      data-tracking-parent={creativeId}
      {...(!isEmpty(styles) && { style: { ...styles } })}
    >
      {!isShop4713Enabled && <hr className="d-block" style={{ marginTop: 0, marginBottom: '24px' }} />}
      <UnifiedLeadForm {...topEmbeddedLeadFormProps} leadTargets={leadTargets} />
    </section>
  );
}

EmbeddedLeadFormUI.propTypes = {
  params: PropTypes.shape({
    zipCode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    vin: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isNewVin: PropTypes.bool,
    tcpaParentDealershipName: PropTypes.string,
    dealerName: PropTypes.string,
    type: PropTypes.string,
    submodelIdentifier: PropTypes.string,
    isPartnerListing: PropTypes.bool,
    partnerCode: PropTypes.string,
    isLease: PropTypes.bool,
  }).isRequired,
  options: PropTypes.shape({
    pathname: PropTypes.string,
    unifiedLeadFormCTA: PropTypes.string,
    daysToSell: PropTypes.number,
    buyonline: PropTypes.bool,
    hideContactDealerTip: PropTypes.bool,
    isShop4713Enabled: PropTypes.bool,
    saveLeadDataForPrequal: PropTypes.func,
    shop4713CreativeId: PropTypes.string,
    isSimulatedLeadForm: PropTypes.bool,
  }).isRequired,
  pageNameLegacy: PropTypes.string,
  className: PropTypes.string,
  isMobile: PropTypes.bool,
  leadTargets: PropTypes.arrayOf(PropTypes.shape({})),
  isVdp: PropTypes.bool,
  isShop5146NciChallenge: PropTypes.bool,
  styles: PropTypes.shape({}),
  isUserSetZip: PropTypes.bool,
  location: PropTypes.shape({}),
  stateCode: PropTypes.string,
};

const mapStateToProps = state => ({
  pageNameLegacy: get(state, 'pageContext.legacy.pageName') || get(state, 'pageContext.page.name'),
  isMobile: state.mobile,
  isUserSetZip: !!get(state, 'visitor.location.userSet'),
  location: get(state, 'pageContext.location'),
});

const stateToPropsConfig = {
  leadTargets: bindToPath(
    ({ params: { radius, vin } }) =>
      buildLeadTargetsPath({
        omitYear: true,
        omitStyleId: true,
        strategy: STRATEGIES.SINGLE,
        radius,
        vin,
      }),
    LeadTargetsModel,
    null,
    false
  ),
  stateCode: bindToPath('location.stateCode', VisitorModel),
};

export const EmbeddedLeadForm = connect(mapStateToProps)(connectToModel(EmbeddedLeadFormUI, stateToPropsConfig));
